import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { UrlBuilderService } from '@shared/services/url-builder.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { forkJoin, from, Observable } from 'rxjs';
import { FeatureStoreService } from '@shared/services/feature-store/feature-store.service';
import { FormBuilder, FormGroup, UntypedFormArray, UntypedFormGroup, Validators } from '@angular/forms';
import { AppConstants } from '@core/constants';
import { AthletesService } from '@core/services/athletes-management/athletes.service';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { SessionService } from '@core/services/session.service';
import {
  IEconomyEfficiencySettings,
  IEconomyEfficiencySettingsUnit,
} from '@shared/interfaces/economy-efficiency-settings.interface';
import { ECONOMY_EFFICIENCY_SETTINGS } from '@shared/constant-list/economy-efficiency-settings.constant';
import { HttpErrorResponse } from '@angular/common/http';
import { HelperService } from '@shared/services/helper.service';
import { DEFAULT_CUSTOM_ATHLETE_CATEGORY_LIST } from '@shared/constant-list/default-custom-athlete-category.constant';

@Component({
  selector: 'app-sport-configuration',
  templateUrl: './sport-configuration.component.html',
  styleUrls: ['./sport-configuration.component.scss'],
})
export class SportConfigurationComponent implements OnInit {
  private urlBuilder: UrlBuilderService = inject(UrlBuilderService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private featureStoreService: FeatureStoreService = inject(FeatureStoreService);
  private fb: FormBuilder = inject(FormBuilder);
  private athletesService: AthletesService = inject(AthletesService);
  private snackBar: MatSnackBar = inject(MatSnackBar);
  private sessionService: SessionService = inject(SessionService);
  private router: Router = inject(Router);
  private helperService: HelperService = inject(HelperService);

  public customUrl: string | undefined;
  public tabIndexToReturn: number = 0;
  public sportConfigurationForm: FormGroup;
  public referenceOptions: Array<any> = [];
  public secondaryReferenceOptions: Array<any> = [];
  public athleteCategoryOptions: any;
  public featureId: string;
  public economyEfficientSettingsConfig: IEconomyEfficiencySettings[] = ECONOMY_EFFICIENCY_SETTINGS;
  public economyEfficientSettingsControl: IEconomyEfficiencySettings & IEconomyEfficiencySettingsUnit;
  public formConditions: UntypedFormGroup = {} as UntypedFormGroup;
  public params: any;
  public sportName: string;
  public isOwner: boolean;
  public customAthleteCategoryListFromAPI: any = [];

  public ngOnInit(): void {
    this.initForm();
    this.subscribeToQueryParams();
    this.initCurrentPage();
    this.isOwner = this.sessionService.auth.role.is_owner;
  }

  public subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (params: Params) => {
        this.params = params;
        this.sportName = this.params.feature_name;
      },
    });
  }

  public initCurrentPage(): void {
    let observableList: Observable<any>[] = [];

    if (this.params.configuration === 'null') {
      observableList = [this.athletesService.getAthleteTrainingLevel()];
    } else {
      observableList = [
        this.athletesService.getAthleteTrainingLevel(),
        this.featureStoreService.getFeatureConfigurationById(this.params.configuration),
      ];
    }

    forkJoin(observableList)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([athleteTrainingLevel, featureConfigurationData]: any): void => {
          this.setTabIndexToReturn(this.params.tab_index_to_return);
          this.setReferenceOption();
          this.customUrl = this.urlBuilder.getCreatedCustomUrl(
            '/home/my-inscyd',
            'tab_index_to_return',
            this.tabIndexToReturn
          );
          this.featureId = this.params.feature_id;

          if (athleteTrainingLevel) {
            this.athleteCategoryOptions = athleteTrainingLevel.items
              .filter(
                (item: any) => !item.is_default_level && !DEFAULT_CUSTOM_ATHLETE_CATEGORY_LIST.includes(item.value)
              )
              .map((item: any) => {
                return this.mappingOptionValue(item, 'value');
              });
          }

          if (featureConfigurationData) {
            let sportConfigurationObject: any = this.helperService.makeCopy(featureConfigurationData.data);
            let updatedSportConfigurationObject = this.updateCustomAthleteCategoryList(
              this.athleteCategoryOptions,
              sportConfigurationObject
            );

            this.setCustomAthleteCategoryFromApi(featureConfigurationData.data.custom_athlete_category);
            this.fillSportConfigurationForm(updatedSportConfigurationObject);
            this.fillEconomyEfficientSettings();

            if (
              featureConfigurationData.data.hasOwnProperty('conditions') &&
              featureConfigurationData.data.conditions.length
            ) {
              featureConfigurationData.data.conditions.forEach((condition: any) => {
                this.conditions.push(this.createNewConditions(condition));
              });
            }

            this.updateCurrentCustomAthleteCategoryFormControl();
          }
        },
        error: (error: any) => {
          if (error !== 'Error: Resource not found or no permission.') {
          }
          this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
        },
      });
  }

  public setCustomAthleteCategoryFromApi(customAthleteCategoryList: any): void {
    this.customAthleteCategoryListFromAPI = customAthleteCategoryList;
  }

  public getCustomAthleteCategoryFromApi(): any {
    return this.customAthleteCategoryListFromAPI;
  }

  public updateCurrentCustomAthleteCategoryFormControl(): void {
    const currentCustomList = this.sportConfigurationForm.get('custom_athlete_category')?.value;
    const customCategoryOptionList: string[] = this.athleteCategoryOptions.map((option: any) => option.name);
    const filteredCustomAthleteList = currentCustomList.filter((item: any) =>
      customCategoryOptionList.includes(item.name)
    );

    this.sportConfigurationForm.get('custom_athlete_category')?.setValue(filteredCustomAthleteList);
  }

  public removeConditionById(id: number, event: Event): void {
    event.stopPropagation();
    this.conditions.removeAt(id);
  }

  public updateEconomyEfficientSettings(event: any): void {
    this.sportConfigurationForm.get('economy_efficient_settings')?.setValue(event.item);
  }

  public initForm(): void {
    this.sportConfigurationForm = this.fb.group({
      primary_type: ['', Validators.required],
      secondary_type: ['', Validators.required],
      custom_athlete_category: [],
      economy_efficient_settings: [],
      default_name: [''],
      ede_factor_a: ['', Validators.required],
      ede_factor_b: ['', Validators.required],
      ede_factor_d: ['', Validators.required],
      per_watt_qw_a: ['', Validators.required],
      per_watt_qw_b: ['', Validators.required],
      per_watt_qw_d: ['', Validators.required],
      gross_efficiency: ['', { disabled: true }],
      lactate_profile_curve: [],
      performance_development: [],
    });

    this.formConditions = this.fb.group({
      condition_name: ['', Validators.required],
      ede_factor_a: ['', Validators.required],
      ede_factor_b: ['', Validators.required],
      ede_factor_d: ['', Validators.required],
      per_watt_qw_eq_a: ['', Validators.required],
      per_watt_qw_eq_b: ['', Validators.required],
      per_watt_qw_eq_d: ['', Validators.required],
      gross_efficiency: ['', { disabled: true }],
      conditions: this.fb.array([]),
    });
  }

  public fillSportConfigurationForm(data: any): void {
    this.sportConfigurationForm.patchValue(data);
  }

  public updateCustomAthleteCategoryList(athleteTrainingLevel: any, sportConfigurationObject: any): any {
    if (
      sportConfigurationObject.hasOwnProperty('custom_athlete_category') &&
      sportConfigurationObject.custom_athlete_category
    ) {
      sportConfigurationObject['custom_athlete_category'].forEach((category: any) => {
        const matchTrainingLevel: any = athleteTrainingLevel.find(
          (trainingLevel: any) => trainingLevel.value === category.value
        );

        if (matchTrainingLevel) {
          category.name = matchTrainingLevel.name;
        }
      });
    } else {
      sportConfigurationObject.custom_athlete_category = [];
    }

    return sportConfigurationObject;
  }

  public fillEconomyEfficientSettings(): void {
    if (this.sportConfigurationForm.get('economy_efficient_settings')?.value) {
      this.economyEfficientSettingsControl = this.sportConfigurationForm.get('economy_efficient_settings')?.value;
    }
  }

  public setReferenceOption(): void {
    this.referenceOptions = [...AppConstants.REFERENCE_SYSTEMS_SPEED, ...AppConstants.REFERENCE_SYSTEMS_POWER];
    this.secondaryReferenceOptions = [
      { id: 0, name: 'None', unit: '' },
      ...AppConstants.REFERENCE_SYSTEMS_SPEED,
      ...AppConstants.REFERENCE_SYSTEMS_POWER,
    ];
  }

  private mappingOptionValue(item: any, param: string) {
    return {
      value: item?.id,
      name: item[param],
    };
  }

  public setTabIndexToReturn(returnIndex: number): void {
    this.tabIndexToReturn = returnIndex;
  }

  public back(): void {
    if (this.customUrl) {
      this.router.navigateByUrl(this.customUrl);
    }
  }

  public save(): void {
    if (this.params.configuration !== 'null') {
      const hasCustomAthleteCategoryListFromApi: any[] = this.getCustomAthleteCategoryFromApi();
      let data: any = hasCustomAthleteCategoryListFromApi?.length
        ? this.getSportConfigPayloadWithModifiedCustomCategory()
        : this.getSportConfigPayload();

      this.featureStoreService.updateFeatureConfiguration({ data }, this.params.configuration).subscribe({
        next: (res) => {
          if (res.data.hasOwnProperty('custom_athlete_category')) {
            this.setCustomAthleteCategoryFromApi(res.data.custom_athlete_category);
          }
          this.snackBar.open(
            'The configuration has been successfully updated',
            'OK',
            AppConstants.TOAST_CONFIG.SUCCESS
          );
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 400) {
            try {
              const message = error.error['detail'];
              return this.snackBar.open(message, 'OK', AppConstants.TOAST_CONFIG.ERROR);
            } catch (e) {
              console.error(e);
              return this.snackBar.open(
                'The configuration has not been successfully updated',
                'OK',
                AppConstants.TOAST_CONFIG.ERROR
              );
            }
          }
          return this.snackBar.open(
            'The configuration has not been successfully updated',
            'OK',
            AppConstants.TOAST_CONFIG.ERROR
          );
        },
      });
    } else {
      const payloadConfiguration: any = {
        data: this.getSportConfigPayload(),
        feature: +this.featureId,
        organization: JSON.parse(this.sessionService.getCookie('auth')).organization.id,
      };

      this.featureStoreService.createFeatureConfiguration(payloadConfiguration).subscribe({
        next: (res: any) => {
          let queryParams: Record<string, null | number | undefined> = {
            tab_index_to_return: this.tabIndexToReturn,
            configuration: res.id,
            feature_id: +this.featureId,
          };

          if (res.data.hasOwnProperty('custom_athlete_category')) {
            this.setCustomAthleteCategoryFromApi(res.data.custom_athlete_category);
          }

          from(this.router.navigate(['/home/sport-configuration'], { queryParams })).subscribe({
            next: (res: any) => {
              this.params.configuration = res.id;
            },
          });
          this.snackBar.open(
            'The configuration has been successfully created',
            'OK',
            AppConstants.TOAST_CONFIG.SUCCESS
          );
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 400) {
            try {
              const message = error.error['detail'];
              return this.snackBar.open(message, 'OK', AppConstants.TOAST_CONFIG.ERROR);
            } catch (e) {
              console.error(e);
              return this.snackBar.open(
                'The configuration has not been successfully created',
                'OK',
                AppConstants.TOAST_CONFIG.ERROR
              );
            }
          }
          return this.snackBar.open(
            'The configuration has not been successfully created',
            'OK',
            AppConstants.TOAST_CONFIG.ERROR
          );
        },
      });
    }
  }

  public getSportConfigPayload(): any {
    return { ...this.sportConfigurationForm.value, conditions: this.formConditions.get('conditions')?.value };
  }

  public getSportConfigPayloadWithModifiedCustomCategory(): any {
    return {
      ...this.sportConfigurationForm.value,
      custom_athlete_category: this.customAthleteCategoryListFromAPI,
      conditions: this.formConditions.get('conditions')?.value,
    };
  }

  public onSelectCustomCategory(event: Event): void {
    this.sportConfigurationForm.get('custom_athlete_category')?.setValue(event);
  }

  public onSingleSelectCustomCategory(event: any): void {
    this.customAthleteCategoryListFromAPI.push(event);
  }

  public onRemovedChipsItem(event: any): void {
    this.customAthleteCategoryListFromAPI = this.customAthleteCategoryListFromAPI.filter(
      (category: any) => category.value !== event.option.value
    );
    this.customAthleteCategoryListFromAPI = [...this.customAthleteCategoryListFromAPI];
  }

  public addCondition(): void {
    this.conditions.push(this.createNewConditions(this.formConditions.value));
  }

  public get conditions(): UntypedFormArray {
    return this.formConditions.get('conditions') as UntypedFormArray;
  }

  public createNewConditions(data: any): UntypedFormGroup {
    return this.fb.group({
      condition_name: [data.condition_name],
      ede_factor_a: [data.ede_factor_a],
      ede_factor_b: [data.ede_factor_b],
      ede_factor_d: [data.ede_factor_d],
      per_watt_qw_eq_a: [data.per_watt_qw_eq_a],
      per_watt_qw_eq_b: [data.per_watt_qw_eq_b],
      per_watt_qw_eq_d: [data.per_watt_qw_eq_d],
      gross_efficiency: [data.gross_efficiency, { disabled: true }],
    });
  }

  public getWattEqBValue(form: any): number {
    return form.controls?.per_watt_qw_b?.value ?? form.controls?.per_watt_qw_eq_b?.value;
  }

  private capitalize = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

  private unslugify = (slug: string): string => slug.split('_').map(this.capitalize).join(' ');

  public customConditionDescription(condition_name?: string): string {
    if (!condition_name) {
      return '';
    }
    return this.unslugify(condition_name);
  }

  private allFieldsHaveValues = <T extends Record<string, unknown>>(obj: T, fields: (keyof T)[]): boolean =>
    fields.some((field) => obj[field] !== null && obj[field] !== undefined);

  public showCustomConditionInputs(condition: Record<string, any>): boolean {
    // To be deprecated
    const requiredFieldsOld = [
      'ede_factor_a',
      'ede_factor_b',
      'ede_factor_d',
      'per_watt_qw_eq_a',
      'per_watt_qw_eq_b',
      'per_watt_qw_eq_d',
    ];
    // Future use
    const requiredFieldsNew = [
      'ebg_factor_a',
      'ebg_factor_b',
      'ebg_factor_d',
      'o2_per_watt_eq_a',
      'o2_per_watt_eq_b',
      'o2_per_watt_eq_d',
    ];
    return this.allFieldsHaveValues(condition, requiredFieldsOld);
  }
}
